import { Bot, Check, Import, MoonStar, Notebook, PenLine, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";

interface NotebookEmptyStateProps {
  onWriteNote: () => void;
  onAskAgent: (prompt: string) => void;
  /** Opens the markdown import flow — omit to hide the affordance. */
  onImport?: () => void;
}

const STARTER_PROMPTS = [
  "Research my top 3 competitors",
  "Summarize this week's customer emails",
  "Draft an outreach plan for enterprise leads",
];

const HOW_IT_WORKS = [
  { icon: PenLine, text: "You write, your agent suggests" },
  { icon: MoonStar, text: "It keeps working while you're away" },
  { icon: Check, text: "You review — keep, edit, or discard" },
];

/** Ghosted preview cards — how the notebook will look once it fills. */
const PREVIEW_NOTES = [
  {
    title: "Competitor pricing — weekly",
    snippet: "Three of five raised list prices this quarter…",
    byAgent: true,
  },
  {
    title: "Enterprise lead strategy",
    snippet: "Focus on 500+ seats. Lead with the security story…",
    byAgent: false,
  },
  {
    title: "Churn interview notes",
    snippet: "Onboarding was the most common sticking point…",
    byAgent: true,
  },
];

/**
 * First-run state of the Notebook — the shared, co-authored knowledge space
 * between the user and their agent. Renders as the inner content of an
 * existing panel (FloatingWindow / bottom sheet); no chrome of its own.
 */
export function NotebookEmptyState({ onWriteNote, onAskAgent, onImport }: NotebookEmptyStateProps) {
  const reduced = useReducedMotion() ?? false;

  const rise = (delay: number) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.35, delay, ease: "easeOut" as const },
        };

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center px-2 py-6 text-center">
      <motion.div
        {...rise(0)}
        className="flex h-14 w-14 items-center justify-center rounded-2xl bg-home-ink/[0.05] dark:bg-white/[0.07]"
      >
        <Notebook className="h-7 w-7 text-home-ink/60 dark:text-white/60" strokeWidth={1.8} />
      </motion.div>

      <motion.h2
        {...rise(0.05)}
        className="mt-5 font-heading text-xl font-bold text-home-ink dark:text-white"
      >
        A notebook you and your agent share
      </motion.h2>
      <motion.p
        {...rise(0.1)}
        className="mt-2 max-w-sm text-sm leading-relaxed text-home-ink/60 dark:text-white/60"
      >
        Notes, research, and documents live here. Your agent adds to it as it
        works — even overnight — and nothing changes without your ok.
      </motion.p>

      <motion.button
        {...rise(0.15)}
        onClick={onWriteNote}
        className="mt-6 flex min-h-[48px] items-center gap-2.5 rounded-full bg-home-yellow px-7 text-[15px] font-bold text-home-ink shadow-[0_10px_28px_-12px_rgba(33,31,28,0.4)] transition-transform hover:scale-[1.02] active:scale-[0.98]"
      >
        <PenLine className="h-[18px] w-[18px]" strokeWidth={2.2} />
        Write your first note
      </motion.button>

      <motion.div {...rise(0.2)} className="mt-5 w-full">
        <p className="text-xs font-medium text-home-ink/45 dark:text-white/45">
          or hand your agent a starting point
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {STARTER_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => onAskAgent(prompt)}
              className="flex min-h-[40px] items-center gap-1.5 rounded-full border border-home-ink/15 px-4 text-[13px] font-medium text-home-ink/70 transition-colors hover:border-brand-coral/50 hover:text-brand-coral-dark active:bg-brand-coral/5 dark:border-white/15 dark:text-white/70 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
            >
              <Sparkles className="h-3.5 w-3.5 shrink-0" strokeWidth={2.2} />
              {prompt}
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div
        {...rise(0.25)}
        className="mt-8 grid w-full gap-2 rounded-2xl border border-home-ink/10 p-4 sm:grid-cols-3 dark:border-white/10"
      >
        {HOW_IT_WORKS.map(({ icon: Icon, text }) => (
          <div
            key={text}
            className="flex items-center gap-2.5 text-left sm:flex-col sm:items-center sm:gap-2 sm:text-center"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-home-ink/[0.05] dark:bg-white/[0.07]">
              <Icon className="h-4 w-4 text-home-ink/55 dark:text-white/55" strokeWidth={2} />
            </span>
            <span className="text-xs leading-snug text-home-ink/60 dark:text-white/60">
              {text}
            </span>
          </div>
        ))}
      </motion.div>

      <motion.div {...rise(0.3)} className="mt-8 w-full" aria-hidden="true">
        <div className="space-y-2 opacity-45 select-none">
          {PREVIEW_NOTES.map((note) => (
            <div
              key={note.title}
              className="flex items-start justify-between gap-3 rounded-xl border border-dashed border-home-ink/20 px-4 py-3 text-left dark:border-white/20"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] font-bold text-home-ink/70 dark:text-white/70">
                  {note.title}
                </div>
                <div className="mt-0.5 truncate text-xs text-home-ink/50 dark:text-white/50">
                  {note.snippet}
                </div>
              </div>
              {note.byAgent && (
                <span className="flex shrink-0 items-center gap-1 rounded-full bg-brand-coral/10 px-2 py-0.5 text-[10px] font-bold text-brand-coral-dark dark:text-brand-coral">
                  <Bot className="h-3 w-3" strokeWidth={2.2} />
                  agent
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="mt-2.5 text-center text-xs text-home-ink/40 dark:text-white/40">
          This is how it fills up.
        </p>
      </motion.div>

      {/* Bring-your-own — markdown import from Notion, Obsidian, or anywhere.
          Connectors are named only here, never as the surface itself. */}
      {onImport && (
        <motion.div
          {...rise(0.35)}
          className="mt-8 flex w-full flex-col items-center gap-1.5 border-t border-home-ink/10 pt-6 dark:border-white/10"
        >
          <button
            onClick={onImport}
            className="flex items-center gap-2 rounded-full border border-home-ink/15 px-4 py-2 text-[13px] font-bold text-home-ink/75 transition-colors hover:border-brand-coral/50 hover:text-brand-coral-dark dark:border-white/15 dark:text-white/75 dark:hover:border-brand-coral/60 dark:hover:text-brand-coral"
          >
            <Import className="h-4 w-4" strokeWidth={2.2} />
            Import markdown from Notion or Obsidian
          </button>
          <span className="text-[11px] text-home-ink/40 dark:text-white/40">
            One-way import · your notes stay plain markdown
          </span>
        </motion.div>
      )}
    </div>
  );
}
